import {
  Box,
  Button,
  Divider,
  Skeleton,
  Typography,
  styled,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useUserAuth } from "../../context/UserAuthContext";
import { useCreateInterest } from "../../hooks/tenants/useCreateInterest";
import theme from "../../styles/styleTheme";
import BookViewing from "../Events/BookViewing";
import LoginOrCreateAccount from "../elements/LoginOrCreateAccount";
import { useEvents } from "../../hooks/events/useEvents";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useTranslation } from "react-i18next";
import NAV from "../../utils/nav.const";
import { useUnsubscribeFromProperty } from "../../hooks/tenants/useUnsubscribeFromProperty";
import { useStages } from "../../hooks/tenants/useStages";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { Modal } from "../common/Modal";
import { BoxPaper } from "../common/BoxPaper";

type PropertyDetailsViewingProps = {
  property: any;
};

const PropertyDetailsViewing = ({ property }: PropertyDetailsViewingProps) => {
  const { t } = useTranslation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isInterested, setIsInterested] = useState(true);
  const [hasViewing, setHasViewing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [unsubscribeAll, setUnsubscribeAll] = useState(false);
  const { user, reloadUserData } = useUserAuth();
  const { createInterest } = useCreateInterest();
  const {
    getDataById: getEventsByPropertyId,
    data: events,
    loading: loadingEvents,
  } = useEvents();
  const { unsubscribeFromProperty } = useUnsubscribeFromProperty();
  const { getData: getStages, data: stages, getStageByName } = useStages();

  useEffect(() => {
    if (user) {
      getStages();
    }
  }, [user]);

  const location = useLocation();
  const redirectTo = location.pathname + location.search;

  useEffect(() => {
    setIsLoggedIn(user != null);

    if (user?.tenant?.candidatures && stages) {
      const inactiveStage = getStageByName("Inactive");
      if (!inactiveStage) return;

      const interested = user.tenant.candidatures.some(
        (c: any) =>
          c.propertyId === property.id && c.stageId !== inactiveStage.id
      );
      setIsInterested(interested);
    }

    if (property?.id) {
      getEventsByPropertyId(property.id);
    }
  }, [stages, user, property]);

  useEffect(() => {
    if (events.length > 0) {
      setHasViewing(true);
    }
  }, [events]);

  const handleShowInterest = async () => {
    const sendEmail = true;
    await createInterest(user.tenant.id, property.id, sendEmail);
    await reloadUserData();
  };

  const handleUnsubscribe = async (allCollections: boolean) => {
    await unsubscribeFromProperty(user.id, property.id, allCollections);
    reloadUserData();
  };

  const confirmationText =
    property.type.name === "Collection" && unsubscribeAll
      ? t("viewing.modal.confirmationUnsubscribeAll")
      : property.type.name === "Property"
      ? t("viewing.modal.confirmationProperty")
      : t("viewing.modal.confirmationCollection");

  const confirmationTitle =
    property.type.name === "Collection" && unsubscribeAll
      ? t("viewing.confirm_unsubscribe_all_title")
      : property.type.name === "Property"
      ? t("viewing.confirm_unsubscribe_title", {
          type: t("viewing.property"),
        })
      : t("viewing.confirm_unsubscribe_title", {
          type: t("viewing.collection"),
        });

  return (
    <BoxPaper
      style={{
        marginTop: "24px",
        background: property?.isRented ? theme.palette.primary.light : "#fff",
      }}
    >
      {!property?.isRented && (
        <Typography variant="h3" marginBottom={1}>
          {t("viewing.plan_viewing")}
        </Typography>
      )}
      {loadingEvents ? (
        <>
          <Skeleton variant="text" width={"80%"} height={30} />
          <Skeleton variant="text" width={"90%"} height={30} />
          <Skeleton variant="text" width={"60%"} height={30} />
          <Skeleton variant="text" width={"85%"} height={30} />
        </>
      ) : (
        <>
          <>
            {property?.isRented ? (
              <Box
                display={"flex"}
                justifyContent={"center"}
                alignItems={"center"}
                color={"#fff"}
              >
                <Box>
                  <ErrorOutlineIcon color="error" sx={{ marginRight: 1 }} />
                </Box>
                <Typography color={"#000"} variant="h3">
                  {t("viewing.recently_rented")}
                </Typography>
              </Box>
            ) : (
              <>
                {isLoggedIn ? (
                  <>
                    {!property?.isRented && (
                      <>
                        {hasViewing ? (
                          <BookViewing property={property} />
                        ) : (
                          <>
                            <Typography variant="subtitle2" marginBottom={3}>
                              {isInterested
                                ? t("viewing.subscribeText_subscribed")
                                : t("viewing.subscribeText_default")}
                            </Typography>
                            {!isInterested && (
                              <Button
                                onClick={handleShowInterest}
                                variant="contained"
                                color="secondary"
                                sx={{
                                  width: "100%",
                                  bgcolor: theme.palette.secondary.main,
                                  color: "#fff",
                                }}
                              >
                                {t("viewing.show_interest")}
                              </Button>
                            )}

                            <Box
                              display={"flex"}
                              flexDirection={"column"}
                              gap={1}
                            >
                              {isInterested && (
                                <Button
                                  onClick={() => {
                                    setUnsubscribeAll(false);
                                    setConfirmOpen(true);
                                  }}
                                  variant="outlined"
                                  color="secondary"
                                  startIcon={<MailOutlineIcon />}
                                  sx={{ width: "100%" }}
                                >
                                  {t("viewing.unsubscribe")}
                                  {t(
                                    `viewing.${property.type.name.toLowerCase()}`
                                  )}
                                </Button>
                              )}

                              {isInterested &&
                                property.type.name === "Collection" && (
                                  <Button
                                    onClick={() => {
                                      setUnsubscribeAll(true);
                                      setConfirmOpen(true);
                                    }}
                                    variant="outlined"
                                    color="secondary"
                                    startIcon={<MailOutlineIcon />}
                                    sx={{ width: "100%" }}
                                  >
                                    {t("viewing.unsubscribeAll")}
                                  </Button>
                                )}
                            </Box>
                          </>
                        )}
                      </>
                    )}

                    {user && !user?.isPremium && (
                      <>
                        <Divider
                          sx={{
                            margin: "23px 0",
                            borderBottomWidth: 2.5,
                            borderColor: "#eee",
                          }}
                        />
                        <Box>
                          <>
                            <Typography variant="h3" marginBottom={1}>
                              {t("viewing.upgradeAccount-title")}
                            </Typography>
                            <Typography variant="subtitle2" marginBottom={3}>
                              {t("viewing.upgradeAccount-text")}
                            </Typography>
                            <Button
                              variant="outlined"
                              color="secondary"
                              sx={{ width: "100%" }}
                              component={Link}
                              to={`/${NAV.PREMIUM_UPGRADE}`}
                            >
                              {t("viewing.upgradeAccount-button")}
                            </Button>
                          </>
                        </Box>
                      </>
                    )}
                  </>
                ) : (
                  <LoginOrCreateAccount
                    title={t("viewing.login_or_create_account")}
                    redirectTo={redirectTo}
                  />
                )}
              </>
            )}
          </>
        </>
      )}
      <Modal
        title={confirmationTitle}
        description={confirmationText}
        open={confirmOpen}
        size="sm"
        setOpen={setConfirmOpen}
        actions
        submitButtonTitle={t("actions.confirm")}
        closeButtonTitle={t("actions.cancel")}
        onConfirm={() => handleUnsubscribe(unsubscribeAll)}
        headerCloseButton
        onClose={() => setConfirmOpen(false)}
      >
        <style>
          {`
      .MuiDialogActions-root button.MuiButton-contained {
        background-color: ${theme.palette.secondary.main};
        color: white;
        box-shadow: none;
        transition: background-color 0.2s ease;
      }

      .MuiDialogActions-root button.MuiButton-contained:hover {
        background-color: ${theme.palette.secondary.main}E6;
        box-shadow: none;
      }

      .MuiDialogActions-root button.MuiButton-outlined {
        color: ${theme.palette.secondary.main};
        border-color: ${theme.palette.secondary.main};
        transition: background-color 0.2s ease;
      }

      .MuiDialogActions-root button.MuiButton-outlined:hover {
        background-color: ${theme.palette.secondary.main}10;
        border-color: ${theme.palette.secondary.main};
      }

        .MuiDialogContentText-root {
        font-size: 0.875rem;
        color: ${theme.palette.grey[800]};
      }
    `}
        </style>
      </Modal>
    </BoxPaper>
  );
};

export const StyledLink = styled(Link)({
  textDecoration: "none",
  color: "#000",
  fontFamily: "Quicksand, sans-serif",
  fontSize: 14,

  ":hover": {
    color: theme.palette.primary.main,
    textDecoration: "underline",
  },
});

export default PropertyDetailsViewing;
